import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import StepSelection from "./StepSelection";
import StepBasics from "./StepBasics";
import StepContact from "./StepContact";
import StepReview from "./StepReview";
import StepExtras from "./StepExtras";
import StepSuccess from "./StepSuccess";
import { getAllAddOns } from "../../addOns/AddOnsService";
import {
  checkReservationAvailability,
  createReservation,
  estimateReservationPrice,
} from "../../reservation/ReservationServices";
import { createAppointment } from "../../appointment/AppointmentServices";
import FacilityConstants from "../../../utilities/constants/FacilityConstants";
import KeycloakService from "../../config/KeycloakService";
import { useUserRefresh, useUser } from "../../user/UserProvider";
import { useLoadingSpinner } from "../../../utilities/components/LoadingSpinnerProvider";
import Modal from "../../../utilities/components/Modal";
import LocalizedModal from "../../../utilities/components/LocalizedModal";
import { ToastContainer, toast } from "react-toastify";

const createEmptyAvailabilityState = () => ({
  checkedRange: null,
  isChecking: false,
  available: null,
  message: "",
});

const BookingWizard = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitStatus, setSubmitStatus] = useState("idle"); // 'idle' | 'success'
  const [showLoggingDialog, setShowLoggingDialog] = useState(false);

  const setFetchUserFlag = useUserRefresh();
  const setIsLoading = useLoadingSpinner();
  const { userProfile } = useUser();
  const availabilityRequestIdRef = useRef(0);
  const availabilityCacheRef = useRef({
    inFlightRange: null,
    inFlightPromise: null,
  });

  const [formData, setFormData] = useState({
    type: null, // 'reservation' | 'appointment'
    eventType: "",
    startDate: "",
    endDate: "",
    guests: 0,
    addOns: [],
    addOnsTotalCost: 0,
    pricingStrategy: "FULL_DAY", // 'HOURLY' | 'FULL_DAY'
    contact: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      additionalInfo: "",
    },
    details: "",
    priceBreakdown: null,
    totalPrice: 0,
    tax: 0,
    subTotal: 0,
  });

  const rates = {};
  const [addOnsData, setAddOnsData] = useState([]);
  const [loadingAddOns, setLoadingAddOns] = useState(false);
  const [availabilityState, setAvailabilityState] = useState(
    createEmptyAvailabilityState,
  );

  // Auto-fill contact info if logged in
  useEffect(() => {
    if (
      userProfile &&
      (!formData.contact.firstName || !formData.contact.email)
    ) {
      setFormData((prev) => ({
        ...prev,
        contact: {
          ...prev.contact,
          firstName: userProfile.firstName || "",
          lastName: userProfile.lastName || "",
          email: userProfile.email || "",
          phone: userProfile.phone || "",
        },
      }));
    }
  }, [userProfile]);

  // Fetch customer-selectable add-ons on mount.
  useEffect(() => {
    const fetchAddOns = async () => {
      setLoadingAddOns(true);
      const wrapperSetAddOns = (response) => {
        // Handle both paginated ({ content: [...] }) and direct array ([...]) responses
        let data = [];
        if (response && Array.isArray(response)) {
          data = response;
        } else if (Array.isArray(response?.content)) {
          data = response.content;
        }

        setAddOnsData(data);
      };
      await getAllAddOns(wrapperSetAddOns);
      setLoadingAddOns(false);
    };

    if (KeycloakService.isLoggedIn() && userProfile?.userId) {
      fetchAddOns();
    }
  }, [userProfile]);

  const nextStep = () => setCurrentStep((prev) => prev + 1);
  const prevStep = () => setCurrentStep((prev) => prev - 1);

  const resetAvailabilityState = useCallback(() => {
    availabilityRequestIdRef.current += 1;
    availabilityCacheRef.current = {
      inFlightRange: null,
      inFlightPromise: null,
    };
    setAvailabilityState(createEmptyAvailabilityState());
  }, []);

  const resetWizard = () => {
    setSubmitStatus("idle");
    setCurrentStep(1);
    resetAvailabilityState();
    setFormData({
      type: null,
      eventType: "",
      startDate: "",
      endDate: "",
      guests: 0,
      addOns: [],
      addOnsTotalCost: 0,
      pricingStrategy: "FULL_DAY",
      contact: {
        firstName: userProfile?.firstName || "",
        lastName: userProfile?.lastName || "",
        email: userProfile?.email || "",
        phone: userProfile?.phone || "",
        additionalInfo: "",
      },
      details: "",
      priceBreakdown: null,
      totalPrice: 0,
      tax: 0,
      subTotal: 0,
    });
  };

  const signIn = async () => {
    await KeycloakService.doLogin(setFetchUserFlag);
  };

  const getStepContent = (step) => {
    if (submitStatus === "success") {
      return <StepSuccess onReset={resetWizard} />;
    }

    switch (step) {
      case 1:
        return <StepSelection onSelect={handleTypeSelect} />;
      case 2:
        return (
          <StepBasics
            onNext={handleBasicsSubmit}
            onPrev={prevStep}
            initialData={formData}
            rates={rates}
                availabilityState={availabilityState}
                onAvailabilityCheck={handleAvailabilityCheck}
          />
        );
      case 3:
        if (formData.type === "reservation") {
          return (
            <StepExtras
              onNext={handleExtrasSubmit}
              onPrev={prevStep}
              initialData={formData}
              addOnsData={addOnsData}
              loading={loadingAddOns}
            />
          );
        } else {
          return (
            <StepContact
              onNext={handleContactSubmit}
              onPrev={prevStep}
              initialData={formData}
            />
          );
        }
      case 4:
        if (formData.type === "reservation") {
          return (
            <StepContact
              onNext={handleContactSubmit}
              onPrev={prevStep}
              initialData={formData}
            />
          );
        } else {
          return (
            <StepReview
              onSubmit={handleFinalSubmit}
              onPrev={prevStep}
              data={formData}
              availabilityState={availabilityState}
            />
          );
        }
      case 5:
        if (formData.type === "reservation") {
          return (
            <StepReview
              onSubmit={handleFinalSubmit}
              onPrev={prevStep}
              data={formData}
              availabilityState={availabilityState}
            />
          );
        }
        return null;
      default:
        return null;
    }
  };

  const updateFormData = (data) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const calculatePrice = async (newData) => {
    const data = { ...formData, ...newData };
    if (data.type === "reservation") {
      const estimate = await estimateReservationPrice(
        {
          startingDateTime: data.startDate,
          endingDateTime: data.endDate,
          numberOfSeats: data.guests,
          addOns: data.addOns,
          addOnsTotalCost: data.addOnsTotalCost || 0,
          pricingStrategy: data.pricingStrategy || "FULL_DAY",
          taxRate: data.taxRate ?? FacilityConstants.TAX_RATE,
        },
        undefined,
        setIsLoading,
      );

      const breakdown = estimate?.breakdown ?? {};
      const overtimeRate = Number(breakdown.overtimeRate ?? 0);
      const overtimeTotal = Number(breakdown.overtime ?? 0);
      const overtimeHours = overtimeRate > 0 ? overtimeTotal / overtimeRate : 0;

      return {
        totalPrice: Number(estimate?.total ?? 0),
        subTotal: Number(estimate?.subtotal ?? 0),
        tax: Number(estimate?.tax ?? 0),
        taxRate: Number(estimate?.taxRate ?? FacilityConstants.TAX_RATE),
        priceBreakdown: {
          addOnsTotal: Number(breakdown.addOns ?? 0),
          facilityRental: Number(breakdown.facility ?? 0),
          facilityCleaningFees: Number(breakdown.cleaning ?? 0),
          bookedHours: breakdown.bookedHours ?? null,
          hourlyRate: breakdown.hourlyRate ?? null,
          seats: {
            seatPrice: Number(breakdown.seatRate ?? 0),
            seatRateTotal: Number(breakdown.seats ?? 0),
          },
          overtime: {
            hours: overtimeHours,
            totalCost: overtimeTotal,
            overtimeRate,
          },
        },
      };
    }
    return {};
  };

  const handleTypeSelect = (type) => {
    // Check Auth for Reservation
    if (type === "reservation" && !KeycloakService.isLoggedIn()) {
      setShowLoggingDialog(true);
      return;
    }
    resetAvailabilityState();
    updateFormData({ type });
    nextStep();
  };

  const handleAvailabilityCheck = useCallback(async (range, options = {}) => {
    const { forceRecheck = false } = options;

    if (!range?.startDate || !range?.endDate) {
      resetAvailabilityState();
      return { available: null, message: "" };
    }

    const checkedRange = `${range.startDate}|${range.endDate}`;

    if (!forceRecheck) {
      if (
        availabilityCacheRef.current.inFlightRange === checkedRange &&
        availabilityCacheRef.current.inFlightPromise
      ) {
        return availabilityCacheRef.current.inFlightPromise;
      }
    }

    const requestId = ++availabilityRequestIdRef.current;
    setAvailabilityState((prev) => ({
      checkedRange,
      isChecking: true,
      available: prev.checkedRange === checkedRange ? prev.available : null,
      message: prev.checkedRange === checkedRange ? prev.message : "",
    }));

    const availabilityPromise = checkReservationAvailability({
      startingDateTime: range.startDate,
      endingDateTime: range.endDate,
    }).then((result) => {
      const normalizedResult = {
        available: result.available,
        message: result.message || "",
      };

      if (requestId === availabilityRequestIdRef.current) {
        availabilityCacheRef.current = {
          inFlightRange: null,
          inFlightPromise: null,
        };
        setAvailabilityState({
          checkedRange,
          isChecking: false,
          available: normalizedResult.available,
          message: normalizedResult.message,
        });
      }

      return normalizedResult;
    });

    availabilityCacheRef.current = {
      ...availabilityCacheRef.current,
      inFlightRange: checkedRange,
      inFlightPromise: availabilityPromise,
    };

    return availabilityPromise;
  }, [resetAvailabilityState]);

  const handleBasicsSubmit = async (basicsData) => {
    if (formData.type === "reservation") {
      const availability = await handleAvailabilityCheck(basicsData, {
        forceRecheck: true,
      });
      if (availability.available === false) {
        return;
      }
    }

    updateFormData(basicsData);
    nextStep();
  };

  const handleExtrasSubmit = async (extrasData) => {
    try {
      setIsLoading(true);
      const priceUpdates = await calculatePrice({ ...extrasData });
      updateFormData({ ...extrasData, ...priceUpdates });
      nextStep();
    } catch (error) {
      console.error("Failed to estimate reservation price", error);
      toast.error("Unable to estimate reservation pricing right now.");
      setIsLoading(false);
    }
  };

  const handleContactSubmit = (contactData) => {
    updateFormData({
      contact: contactData.contact,
      details: contactData.details,
    });
    nextStep();
  };

  const handleFinalSubmit = async () => {
    setIsLoading(true);
    let apiErrorOccurred = false;

    // API Error/UI State setter wrappers
    const setApiError = (hasError) => {
      if (hasError) {
        apiErrorOccurred = true;
        toast.error("An error occurred during submission.");
      }
    };
    const setState = () => {}; // No-op

    try {
      if (formData.type === "reservation") {
        const availability = await handleAvailabilityCheck({
          startDate: formData.startDate,
          endDate: formData.endDate,
        }, {
          forceRecheck: true,
        });

        if (availability.available === false) {
          apiErrorOccurred = true;
          toast.error(
            availability.message || "That time frame is no longer available.",
          );
          return;
        }

        const payload = {
          startingDateTime: new Date(formData.startDate).toISOString(),
          endingDateTime: new Date(formData.endDate).toISOString(),
          effectiveEndingDateTime: null,
          eventType: formData.eventType,
          numberOfSeats: formData.guests,
          // AddOns: Ensure structure matches what backend deserializer expects for Collection<RequestedAddOn>
          addOns: formData.addOns.map((item) => ({
            addOn: item.addOn,
            quantity: item.quantity,
          })),
          addOnsTotalCost: formData.addOnsTotalCost,
          status: FacilityConstants.STATUS.PENDING,
          fullPackage: false,
          securityDepositRefunded: false,
          taxRate: formData.taxRate ?? FacilityConstants.TAX_RATE,
          totalPrice: formData.totalPrice,
          priceComputationMethod: FacilityConstants.COMPUTATION_METHOD.AUTO,
          pricingStrategy: formData.pricingStrategy || "FULL_DAY",
          userId: KeycloakService.getUserId(),
        };

        await createReservation(payload, setApiError, setState, setIsLoading);
      } else if (formData.type === "appointment") {
        const payload = {
          firstName: formData.contact.firstName,
          lastName: formData.contact.lastName,
          email: formData.contact.email,
          phone: formData.contact.phone,
          dateTime: new Date(formData.startDate).toISOString(),
          raison: "Venue Tour / Meeting",
          status: FacilityConstants.STATUS.BOOKED,
          additionalInfo: formData.details || "",
          userId: KeycloakService.isLoggedIn()
            ? KeycloakService.getUserId()
            : null,
        };
        await createAppointment(payload, setApiError, setState, setIsLoading);
      }
    } catch (error) {
      console.error("Submission failed", error);
      apiErrorOccurred = true;
      toast.error("Submission failed. Please try again.");
    } finally {
      setIsLoading(false);
      if (!apiErrorOccurred) {
        setSubmitStatus("success");
      }
    }
  };

  const totalSteps = formData.type === "reservation" ? 5 : 4;

  return (
    <div className="w-full max-w-5xl mx-auto p-4 md:p-8">
      {/* Auth Modal */}
      <Modal isOpen={showLoggingDialog} onClose={setShowLoggingDialog}>
        <LocalizedModal
          actionHandler={signIn}
          actionText="Sign In"
          cancelText="Go back"
          setOpen={setShowLoggingDialog}
          title="Sign In Required"
          message="Please sign in or register to make a reservation."
        />
      </Modal>

      <ToastContainer style={{ zIndex: 99999999 }} />

      {/* Progress Bar */}
      {submitStatus !== "success" && (
        <div className="mb-8 mt-4 md:mt-0">
          <div className="flex justify-between items-center mb-2 relative z-10">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
              <div
                key={step}
                className={`flex flex-col items-center ${step <= currentStep ? "text-blue-600 dark:text-blue-400" : "text-slate-400 dark:text-slate-600"}`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center font-bold mb-1 transition-colors duration-300 border-2 ${
                    step <= currentStep
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-500"
                  }`}
                >
                  {step}
                </div>
                {/* Optional Labels for non-mobile */}
                <span className="text-xs font-semibold hidden sm:block mt-1">
                  {step === 1 && "Type"}
                  {step === 2 && "Basics"}
                  {step === 3 &&
                    (formData.type === "reservation" ? "Extras" : "Contact")}
                  {step === 4 &&
                    (formData.type === "reservation" ? "Contact" : "Review")}
                  {step === 5 && "Review"}
                </span>
              </div>
            ))}
          </div>
          {/* Simple Progress Bar Overlay */}
          <div className="h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden mt-2 relative -z-0">
            <div
              className="h-full bg-blue-600 transition-all duration-500 ease-out"
              style={{
                width: `${((currentStep - 1) / (totalSteps - 1 || 1)) * 100}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Wizard Content */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 min-h-[500px] p-6 relative overflow-hidden transition-colors duration-300">
        <AnimatePresence mode="wait">
          <motion.div
            key={submitStatus === "success" ? "success" : currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="h-full"
          >
            {getStepContent(currentStep)}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default BookingWizard;
